import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import BrandingEditorLockService from '@services/BrandingEditorLockService';
import ParticipantScopes from '@constants/scopes';
import { IBrandingEditorLockState } from '@interfaces/brandingEditorLock';

import {
  resolveLockHolderName,
  useBrandingEditorLock,
} from './useBrandingEditorLock';

/** Used until the backend has named its own interval. */
export const DEFAULT_HEARTBEAT_INTERVAL_MS = 15_000;

/**
 * How often a hub that sees somebody else's lock re-reads it. The broadcast is
 * the primary signal; this only catches a release the socket missed, so it can
 * be slow.
 */
export const LOCKED_BY_OTHER_POLL_INTERVAL_MS = 30_000;

interface IHeldLock {
  eventId: string;
  sessionId: string;
}

/**
 * Keeps this hub's Editor Mode lock consistent with the backend for as long
 * as the event page is mounted.
 *
 * - Reads the lock when an admin arrives, and again every
 *   `LOCKED_BY_OTHER_POLL_INTERVAL_MS` while somebody else holds it.
 * - Heartbeats while the editor is open; a refused heartbeat means the lock
 *   was taken over or released for a session that left, so the editor closes
 *   with a notification rather than letting two people edit.
 * - Releases when the editor closes by any path (the flag is the single
 *   signal), when the page is left, and — best effort, `keepalive` — when the
 *   tab is closed or reloaded. The backend releases on its own as well when
 *   the session's last hub socket goes, so this is the fast path, not the
 *   only one.
 */
export const useBrandingEditorLockLifecycle = (): void => {
  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();
  const { websocketSessionId, sendMessageToUnreal, userPermissions } =
    useMytaverse();
  const {
    currentEventId,
    participants,
    isBrandingModeEnabled,
    setIsBrandingModeEnabled,
    setRoomSpots,
    setBrandingSpots,
    setCurrentBrandingId,
    setBrandingEditorFocus,
    setBrandingEditorLockState,
  } = useMytaverseEvent();
  const { lockState, isLockedByOther, refresh } = useBrandingEditorLock();

  const hasAdminTools = !!userPermissions?.includes(
    ParticipantScopes.HubAdminTools,
  );
  const heartbeatIntervalMs =
    lockState?.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;

  // What this hub currently holds, kept in a ref so the release effects read
  // the session the lock was taken under rather than whatever the deps hold at
  // cleanup time.
  const heldRef = useRef<IHeldLock | null>(null);

  useEffect(() => {
    if (!hasAdminTools || !currentEventId) {
      return;
    }

    refresh();
  }, [hasAdminTools, currentEventId, refresh]);

  useEffect(() => {
    if (!hasAdminTools || !isLockedByOther) {
      return;
    }

    const interval = setInterval(refresh, LOCKED_BY_OTHER_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [hasAdminTools, isLockedByOther, refresh]);

  // The flag going false is the one place a release lives: the editor has
  // three exit controls and a forced exit below, and none of them needs to
  // know about the lock.
  useEffect(() => {
    if (isBrandingModeEnabled) {
      if (currentEventId && websocketSessionId) {
        heldRef.current = {
          eventId: currentEventId,
          sessionId: websocketSessionId,
        };
      }

      return;
    }

    const held = heldRef.current;
    heldRef.current = null;

    if (!held) {
      return;
    }

    BrandingEditorLockService.releaseLock(held.eventId, held.sessionId)
      .then((state) => {
        if (state) {
          setBrandingEditorLockState(state);
        }
      })
      .catch((error) => {
        // The backend frees it anyway — by heartbeat age, or with the session.
        console.error(
          `Failed to release the branding editor lock: ${
            (error as Error)?.message
          }`,
          { eventId: held.eventId },
        );
      });
  }, [
    isBrandingModeEnabled,
    currentEventId,
    websocketSessionId,
    setBrandingEditorLockState,
  ]);

  // Leaving the event page (unmount) and leaving the tab (`pagehide`) both
  // lose the editor's state, so both give the lock back straight away.
  useEffect(() => {
    const releaseOnPageHide = () => {
      const held = heldRef.current;

      if (held) {
        void BrandingEditorLockService.releaseLock(
          held.eventId,
          held.sessionId,
          {
            keepalive: true,
          },
        );
      }
    };

    window.addEventListener('pagehide', releaseOnPageHide);

    return () => {
      window.removeEventListener('pagehide', releaseOnPageHide);
      releaseOnPageHide();
      heldRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isBrandingModeEnabled || !currentEventId || !websocketSessionId) {
      return;
    }

    let cancelled = false;

    const closeEditorAsLockLost = (state: IBrandingEditorLockState) => {
      // Mirrors AdminTools.handleLeaveBrandingMode; the release effect above
      // then fires a no-op release, which the backend answers idempotently.
      setIsBrandingModeEnabled(false);
      setRoomSpots([]);
      setBrandingSpots([]);
      setCurrentBrandingId(null);
      setBrandingEditorFocus('environment');
      sendMessageToUnreal({ action: 'LEAVE_CUSTOMIZATION_MODE' });
      setBrandingEditorLockState(state);

      // Released for a session that left, or taken over: only the second has
      // somebody to name.
      showNotification({
        message: state.lock
          ? translate(
              'adminToolbox.editorModeLockLost',
              'Editor Mode was closed: {{name}} is editing this experience now.',
              { name: resolveLockHolderName(state.lock, participants) },
            )
          : translate(
              'adminToolbox.editorModeLockLostNoHolder',
              'Editor Mode was closed: your editing session has ended.',
            ),
        type: NOTIFICATION_TYPES.WARNING,
      });
    };

    const beat = async () => {
      try {
        const { held, state } = await BrandingEditorLockService.heartbeat(
          currentEventId,
          websocketSessionId,
        );

        if (cancelled) {
          return;
        }

        if (held) {
          setBrandingEditorLockState(state);
          return;
        }

        closeEditorAsLockLost(state);
      } catch (error) {
        // A missed beat is tolerated by the backend's stale window; only a
        // refusal (handled above) means the lock is gone.
        console.warn(
          `Branding editor lock heartbeat failed: ${(error as Error)?.message}`,
          { eventId: currentEventId },
        );
      }
    };

    const interval = setInterval(beat, heartbeatIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    isBrandingModeEnabled,
    currentEventId,
    websocketSessionId,
    heartbeatIntervalMs,
    participants,
    setIsBrandingModeEnabled,
    setRoomSpots,
    setBrandingSpots,
    setCurrentBrandingId,
    setBrandingEditorFocus,
    setBrandingEditorLockState,
    sendMessageToUnreal,
    showNotification,
    translate,
  ]);
};
